import type { ErrorEvent, EventHint } from '@sentry/react';

/**
 * Detects the "stale chunk" failure: a deploy replaces the content-hashed chunk
 * filenames, so a tab still running the previous bundle requests a chunk URL that
 * no longer exists. Firebase Hosting answers with index.html (its SPA fallback)
 * rather than a 404, and the browser refuses to execute HTML as a module.
 *
 * Both error boundaries reload the page on a positive match (`reloadForStaleChunk`),
 * which is the only recovery: the fresh index.html points at chunk names that do
 * exist. A message
 * this does not recognise instead reaches the user as an error screen showing
 * raw browser text, so the list below must cover every wording a supported
 * browser emits.
 *
 * Each engine words it differently, and they change it between versions - Safari
 * 26 moved from "Importing a module script failed." to the MIME-type phrasing,
 * which stranded Safari users on the error screen until it was added here
 * (Sentry BILL-WEBAPP-BQ; Firefox's wording was the same omission earlier, in
 * BILL-WEBAPP-9V). Prefer matching the shortest stable fragment of a message
 * over its full text, so a reworded prefix or an appended URL still matches.
 */
const STALE_CHUNK_MESSAGES = [
  // Chrome: "Failed to fetch dynamically imported module: <url>"
  // Firefox: "error loading dynamically imported module: <url>"
  'dynamically imported module',
  // Safari (<= 18): "Importing a module script failed."
  'Importing a module script failed',
  // Safari 26: "'text/html' is not a valid JavaScript MIME type."
  'is not a valid JavaScript MIME type',
  // Chrome: "Failed to load module script: Expected a JavaScript module script
  // but the server responded with a MIME type of "text/html". ..."
  'Expected a JavaScript module script',
];

export function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return STALE_CHUNK_MESSAGES.some((msg) => error.message.includes(msg));
}

/**
 * Sentry `beforeSend` hook that drops a stale-chunk error: the boundaries recover
 * from it by reloading, so it is a deploy, not a fault. `Sentry.ErrorBoundary`
 * captures every error before its fallback renders and has no way to opt out
 * (`beforeCapture` can only tag the scope), so every open tab that navigated after
 * a deploy reported one issue per chunk hash (BILL-WEBAPP-GR and siblings).
 * Any other event passes through untouched.
 */
export function dropStaleChunkEvent<T extends ErrorEvent>(event: T, hint: EventHint): T | null {
  return isStaleChunkError(hint.originalException) ? null : event;
}

/**
 * Warms a lazy chunk without awaiting it, for hover/focus prefetches that run
 * before the user has asked for the page. A stale-chunk failure there is not yet
 * the user's problem: the real navigation imports the same module, fails the same
 * way, and the error boundary reloads onto the new deploy. Left unhandled, it
 * instead reached Sentry as an unhandled rejection from every open tab after
 * every deploy (BILL-WEBAPP-6, -9, -F3). Any other failure still rejects, so the
 * caller's `void` surfaces it as an unhandled rejection exactly as before.
 */
export function prefetchChunk(load: () => Promise<unknown>): Promise<void> {
  return load().then(
    () => undefined,
    (error: unknown) => {
      if (!isStaleChunkError(error)) throw error;
    },
  );
}

const RELOAD_STORAGE_KEY = 'stale_chunk_reloaded_at';

/**
 * How long after a stale-chunk reload another one is suppressed. A reload that
 * lands on the same missing chunk fails again within about half a second, so
 * anything inside this window is a loop rather than a second deploy.
 */
export const STALE_CHUNK_RELOAD_COOLDOWN_MS = 10_000;

/**
 * True when this tab already reloaded for a stale chunk within the cooldown, so
 * another reload would be a loop. Read-only, for deciding what to render.
 */
export function isStaleChunkReloadSuppressed(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_STORAGE_KEY));
    return last > 0 && now - last < STALE_CHUNK_RELOAD_COOLDOWN_MS;
  } catch {
    // Storage unavailable (blocked site data, quota): no guard, see reloadForStaleChunk.
    return false;
  }
}

/**
 * Reloads onto the current deploy, unless this tab already did so within the
 * cooldown. Without the guard, a chunk that stays unreachable after the reload
 * reloads the page forever: during a hosting release roll-out one tab reloaded
 * twice a second for 12 seconds, reporting each round to Sentry (BILL-WEBAPP-ED).
 * Declining leaves the boundary's fallback on screen, with its own reload action.
 *
 * Returns whether it reloaded. When sessionStorage is unavailable it reloads
 * anyway: losing the loop guard for those tabs is the lesser harm next to
 * stranding them on the error screen after every deploy.
 */
export function reloadForStaleChunk(reload: () => void = () => window.location.reload()): boolean {
  const now = Date.now();
  if (isStaleChunkReloadSuppressed(now)) return false;
  try {
    sessionStorage.setItem(RELOAD_STORAGE_KEY, String(now));
  } catch {
    // Storage unavailable (blocked site data, quota): reload unguarded, per above.
  }
  reload();
  return true;
}
